import { useMemo } from 'react';

import { faTooth } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import { round } from 'lodash';

import type { TVisitSummary } from '@/react-query/useVisitSummaries';
import { useUniversalToFDI } from '@/utils/internationalization/useUniversalToFDI';

import type { ImageObject } from '../types';

import { getToothHasBoneLevelMeasurements } from './helpers';

interface Props {
  tooth: TVisitSummary['teeth'][number];
  activeImage?: ImageObject;
}

const isPercent = (value: number) => 0 <= value && value <= 1;
const showAsMM = (value: number | undefined) =>
  typeof value === 'number' ? `${round(value, 1)}mm` : null;
const showAsPercent = (value: number | undefined) =>
  typeof value === 'number' && isPercent(value)
    ? `${round(value * 100, 1)}%`
    : null;

export const ToothBoneLevels = ({ tooth, activeImage }: Props) => {
  const { displayToothNumber } = useUniversalToFDI();
  const { t } = useLingui();

  const {
    toothId,
    distBwAblDistalToCejDistal,
    distBwAblMesialToCejMesial,
    paDistalBoneLevel,
    paMesialBoneLevel,
  } = tooth;
  const noMeasurementMessage = t`Unable to calculate bone level`;
  const hasBoneLevelMeasurements = getToothHasBoneLevelMeasurements(tooth);

  // for BW images, use boneLevelDistal and boneLevelMesial, which are in mm
  // for periapical images, use paDistalBoneLevel and paMesialBoneLevel, which are in percent
  // if we have an active image and if it's a PA image, prioritize %
  const distalBoneLevel = useMemo(() => {
    if (activeImage?.detectedImageType === 'PERIAPICAL') {
      return (
        showAsPercent(paDistalBoneLevel) ??
        showAsMM(distBwAblDistalToCejDistal) ??
        noMeasurementMessage
      );
    }
    return (
      showAsMM(distBwAblDistalToCejDistal) ??
      showAsPercent(paDistalBoneLevel) ??
      noMeasurementMessage
    );
  }, [
    distBwAblDistalToCejDistal,
    activeImage?.detectedImageType,
    noMeasurementMessage,
    paDistalBoneLevel,
  ]);

  const mesialBoneLevel = useMemo(() => {
    if (activeImage?.detectedImageType === 'PERIAPICAL') {
      return (
        showAsPercent(paMesialBoneLevel) ??
        showAsMM(distBwAblMesialToCejMesial) ??
        noMeasurementMessage
      );
    }

    return (
      showAsMM(distBwAblMesialToCejMesial) ??
      showAsPercent(paMesialBoneLevel) ??
      noMeasurementMessage
    );
  }, [
    distBwAblMesialToCejMesial,
    activeImage?.detectedImageType,
    noMeasurementMessage,
    paMesialBoneLevel,
  ]);

  if (!hasBoneLevelMeasurements) return null;

  return (
    <div className="vai-grid vai-grid-cols-[1fr_2.5fr] vai-items-start vai-border-b vai-pb-1 vai-border-b-zinc-700">
      <div className="vai-flex vai-items-center vai-gap-1">
        <FontAwesomeIcon icon={faTooth} />
        <p className="vai-typography-small vai-text-white">
          {displayToothNumber(toothId)}
        </p>
      </div>

      <div>
        <p className="vai-font-inter vai-text-[12px] vai-font-medium vai-text-white">
          <Trans>Distal</Trans>: {distalBoneLevel}
        </p>
        <p className="vai-font-inter vai-text-[12px] vai-font-medium vai-text-white">
          <Trans>Mesial</Trans>: {mesialBoneLevel}
        </p>
      </div>
    </div>
  );
};
